import React, { useCallback, useEffect, useState } from 'react';
import Card from 'components/inputs/Card';
import { Spinner, Modal } from 'react-bootstrap';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATE_USER } from 'actions/user';
import { useParams } from 'react-router';
import { UPDATE_STATUS_CODE } from 'components/common/constant';
import { toast } from 'react-toastify';
import { GET_STATION_BY_CITY } from 'actions/chargingStation';
import { GET_CUSTOMER } from 'actions/customer';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CheckBox from 'components/inputs/CheckBox';
import RequestModal from '../partnerManagement/EMSP/RequestModal';

const FleetStationAccess = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { fleetId } = useParams();

  const [showStationModel, setShowStationModel] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [stationSelection, setStationSelection] = useState({});

  const isLoader = useSelector((state) => state.customer.isLoading);
  const allFilterStations = useSelector((state) => state.stationByCity?.stationByCities);
  const userDetail = useSelector((state) => state.customerProfile.customerProfile);

  const userProfileStations = userDetail?.charging_access_on_stations;
  const stationList = !_.isEmpty(userProfileStations) ? allFilterStations.filter((x) => userProfileStations.indexOf(x.id) !== -1) : [];

  const getStations = useCallback(
    (data = {}) => {
      dispatch({ type: GET_STATION_BY_CITY.REQUEST, payload: data });
    },
    [dispatch]
  );

  const getCustomerDetail = useCallback(
    (id) => {
      dispatch({ type: GET_CUSTOMER.REQUEST, payload: id });
    },
    [dispatch]
  );

  useEffect(() => {
    getStations();
    getCustomerDetail(fleetId);
  }, [fleetId, getStations, getCustomerDetail]);

  const updateUser = useCallback(
    (updatedStations) => {
      const userData = {
        charging_access_on_stations: updatedStations || [],
        id: fleetId,
      };

      dispatch({
        type: UPDATE_USER.REQUEST,
        payload: userData,
        cb: (res) => {
          if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
            toast.success('Remove Station(s) successfully');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        },
      });
    },
    [dispatch, fleetId, getCustomerDetail, getStations]
  );

  useEffect(() => {
    setSelectAll(false);
    const shouldSelectAll = stationList?.every((station) => {
      const selection = stationSelection[station.id];
      return selection?.stationChecked === true;
    });
    setSelectAll(shouldSelectAll);
  }, [stationList, stationSelection]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const updatedSelection = {};
    stationList.forEach((station) => {
      updatedSelection[station.id] = {
        stationChecked: newSelectAll,
      };
    });

    setStationSelection((prev) => ({ ...prev, ...updatedSelection }));
  };

  const handleStationSelect = (stationId) => {
    const stationChecked = !stationSelection[stationId]?.stationChecked;

    setStationSelection((prev) => {
      const updatedSelection = {
        ...prev,
        [stationId]: {
          stationChecked,
        },
      };
      updateSelectAll(updatedSelection);
      return updatedSelection;
    });
  };

  const updateSelectAll = (updatedSelection) => {
    const allChecked = stationList.every((station) => {
      const selection = updatedSelection[station.id];
      return selection?.stationChecked === true;
    });
    setSelectAll(allChecked);
  };

  const handleCloseStationModel = () => {
    setShowStationModel(false);
  };

  const getSelectedStationIds = () => {
    return Object.entries(stationSelection)
      .filter((entry) => entry[1].stationChecked)
      .map((entry) => entry[0]);
  };

  const isAnyStationSelected = Object.values(stationSelection).some((selection) => selection.stationChecked);

  return (
    <React.Fragment>
      <Card>
        {!_.isEmpty(stationList) && !(isLoader || _.isUndefined(isLoader)) && (
          <div className="select-all-checkbox-input__block">
            <label className="select-all--label">{t('stationAccessToPatner.selectAll')}</label>
            <CheckBox checked={selectAll} onChange={handleSelectAll} />
          </div>
        )}
        <div className="station-access-list__main">
          <div className="station-access-list--block">
            {isLoader ? (
              <div className="loader--block">
                <Spinner as="span" animation="border" size="sm" role="status" />
                <span className="ps-2">{t('placeHolder.loading')}</span>
              </div>
            ) : _.isEmpty(stationList) ? (
              <div className="empty-data-block">{t('stationAccessToPatner.noData')}</div>
            ) : (
              _.map(stationList, (item) => (
                <ChargerDataItem
                  key={item.id}
                  station={item}
                  stationChecked={stationSelection[item.id]?.stationChecked || false}
                  onStationSelect={() => handleStationSelect(item.id)}
                />
              ))
            )}
          </div>
        </div>
        {!_.isEmpty(stationList) && !(isLoader || _.isUndefined(isLoader)) && (
          <div className="charger-list--button" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowStationModel(true)} disabled={!isAnyStationSelected} style={{ padding: '1rem 5rem' }}>
              {t('button.remove')}
            </Button>
          </div>
        )}
      </Card>

      {showStationModel && (
        <Modal show={showStationModel} centered onHide={handleCloseStationModel}>
          <RequestModal
            subTitle={t('stationAccessToPatner.modelTitle')}
            title={t('stationAccessToPatner.modelTitle')}
            onClose={handleCloseStationModel}
            requestModal={() => {
              const selectedIds = getSelectedStationIds();
              const remainingStations = userDetail?.charging_access_on_stations?.filter((id) => !selectedIds.includes(id));
              updateUser(remainingStations);
              setShowStationModel(false);
            }}
          />
        </Modal>
      )}
    </React.Fragment>
  );
};

const ChargerDataItem = ({ station, stationChecked, onStationSelect }) => {
  const visibleChargers = station.chargers?.filter(
    (c) => c.is_external_charger === false && c.is_active === true
  );
  return (
    <div className="station-access--list">
      <div className="list-info">
        <div className="station-checkbox-input__block">
          <label className="station--label">{station.name || ''}</label>
          <CheckBox checked={stationChecked} onChange={onStationSelect} />
        </div>
        <div className="charger-list-info">
          <div className="charger-checkbox-input__block">
            <div className="charger--label">{visibleChargers?.map((data) => data.charger_id).join(', ')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

ChargerDataItem.propTypes = {
  station: PropTypes.object,
  onStationSelect: PropTypes.func,
  stationChecked: PropTypes.bool,
};

export default FleetStationAccess;
